import classNames from "classnames";
import React from "react";
import { jt, msgid, ngettext, t } from "ttag";

import { constants } from "@reactivated/constants";

import { IWebPageURL } from "../../../models/nominals";
import { trackSleepExpertEvent } from "../../../utils/analytics";
import { formatPhoneNumber } from "../../../utils/format";
import { usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
import { ChatLink } from "../../chat/ChatLink";
import { useAppSelector } from "../../reducers";
import { nearbyStoreSelector, preferredLocationSelector } from "../selectors";

import styles from "./FooterWidgets.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

const ContactSupportWidget = (props: {
    phone: string;
    chatOfflineLink?: IWebPageURL;
}): React.ReactNode => {
    const contactLinkURL = usePageURL("contact_link");
    const homeURL = usePageURL("home");
    const chatOfflineLink = props.chatOfflineLink || contactLinkURL || homeURL;
    const chatLink = (
        <ChatLink key="chat-link" chatOfflineLink={chatOfflineLink} />
    );
    const phoneLink = (
        <a
            key="phone-link"
            className={styles.link}
            href={`tel:${props.phone}`}
            onClick={() => trackSleepExpertEvent("call")}
        >
            {formatPhoneNumber(props.phone)}
        </a>
    );
    return (
        <div className={styles.contactSupport}>
            <h4 className={styles.heading}>
                <ChatLink
                    chatOnlineText={t`Talk to a Sleep Expert`}
                    chatOfflineText={t`Talk to a Sleep Expert`}
                    chatOfflineLink={chatOfflineLink}
                />
            </h4>
            <span className={styles.copy}>
                {jt`${chatLink} or call ${phoneLink}`}
            </span>
        </div>
    );
};

const NearbyStoresWidget = (): React.ReactNode => {
    const appSlug = usePageSetting("app_slug");
    const numStores = useAppSelector(
        (state) => nearbyStoreSelector(state).length,
    );
    const location = useAppSelector((state) =>
        preferredLocationSelector(state),
    );
    const text =
        appSlug === AppSlug.TEMPURCA
            ? t`Find a retailer near you`
            : location
              ? ngettext(
                    msgid`${numStores} store near ${location.formatted_address}`,
                    `${numStores} stores near ${location.formatted_address}`,
                    numStores,
                )
              : ngettext(
                    msgid`${numStores} store near you`,
                    `${numStores} stores near you`,
                    numStores,
                );
    const seeListLink =
        appSlug === AppSlug.TEMPURCA ? null : (
            <a
                key="see-retailer-list-link"
                className={styles.link}
                title={t`Find a Retailer`}
                href="/find-a-retailer"
            >
                {t`See List`}
            </a>
        );

    return (
        <div className={styles.nearbyStores}>
            <h4 className={styles.heading}>
                <a
                    title={t`Find a Retailer`}
                    href="/find-a-retailer"
                    className="footer-widget__header"
                >
                    {t`Feel it for Yourself`}
                </a>
            </h4>
            <span className={styles.copy}>
                <span>{text}</span> {seeListLink}
            </span>
        </div>
    );
};

export const FooterWidgets = (props: {
    phoneNumber: string;
    showContactSupportWidget: boolean;
    showNearbyStoresWidget: boolean;
    chatOfflineLink?: IWebPageURL;
}): React.ReactNode => {
    const firstWidgetClasses = classNames({
        [styles.firstWidget]:
            props.showContactSupportWidget && props.showNearbyStoresWidget,
        [styles.widgetWrapper]: props.showContactSupportWidget,
    });
    if (!props.showContactSupportWidget && !props.showNearbyStoresWidget) {
        return null;
    }
    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                {props.showContactSupportWidget && (
                    <div className={firstWidgetClasses}>
                        <ContactSupportWidget
                            phone={props.phoneNumber}
                            chatOfflineLink={props.chatOfflineLink}
                        />
                    </div>
                )}
                {props.showNearbyStoresWidget && (
                    <div className={styles.widgetWrapper}>
                        <NearbyStoresWidget />
                    </div>
                )}
            </div>
        </div>
    );
};
